import { useEffect, useMemo, useRef, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    FileText,
    IndianRupee,
    Loader2,
    Paperclip,
    Search,
    ShieldAlert,
    Upload,
    Users,
    X,
} from "lucide-react";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import { MaterialsApi } from "api/materials/service";
import { taskApi } from "api/tasks/taskApi";
import { ContractorApi } from "api/contractors/service";
import { ProcurementApi } from "api/procurement/service";
import { resolveTransactionFeedback } from "utils/transactionFeedback";
import { formatDateLong } from "utils/utilityFunctions";
import { getLinkedTasksBySite } from "utils/linkedTasks";
import FileViewer from "components/FileViewer";

const ACTION_BUTTON_LABELS = {
    INVENTORY_UPDATE: "update stock",
    TASK_UPDATE: "post now",
    SUBTASK_UPDATE: "post now",
    WORKER_ATTENDANCE: "mark now",
    CONTRACTOR_ATTENDANCE: "mark now",
    VENDOR_PAYMENT: "pay now",
    CONTRACTOR_PAYMENT: "pay now",
    WORKER_PAYMENT: "pay now",
};

const COMPLETED_ACTION_BUTTON_LABELS = {
    INVENTORY_UPDATE: "update again",
    TASK_UPDATE: "continue update",
    SUBTASK_UPDATE: "continue update",
    WORKER_ATTENDANCE: "review attendance",
    CONTRACTOR_ATTENDANCE: "mark attendance",
    VENDOR_PAYMENT: "continue payment",
    CONTRACTOR_PAYMENT: "continue payment",
    WORKER_PAYMENT: "continue payment",
};

const ACTION_ACCESS_REQUIREMENTS = {
    VENDOR_PAYMENT: {
        scope: "organization",
        permissions: ["finance_control"],
        permissionLabel: "Finance Control",
        actionLabel: "record vendor payments",
        adminLabel: "an organization admin",
    },
    CONTRACTOR_PAYMENT: {
        scope: "organization",
        permissions: ["finance_control"],
        permissionLabel: "Finance Control",
        actionLabel: "record contractor payments",
        adminLabel: "an organization admin",
    },
    CONTRACTOR_ATTENDANCE: {
        scope: "organization",
        permissions: ["contractor_control"],
        permissionLabel: "Contractor Control",
        actionLabel: "mark contractor attendance",
        adminLabel: "an organization admin",
    },
    WORKER_PAYMENT: {
        scope: "site",
        permissions: ["edit_finance"],
        permissionLabel: "Edit Finance",
        actionLabel: "record worker payments",
        adminLabel: "a project admin",
    },
    WORKER_ATTENDANCE: {
        scope: "site",
        permissions: ["edit_worker_management"],
        permissionLabel: "Edit Worker Management",
        actionLabel: "mark worker attendance",
        adminLabel: "a project admin",
    },
    INVENTORY_UPDATE: {
        scope: "site",
        permissions: ["edit_material_management"],
        permissionLabel: "Edit Material Management",
        actionLabel: "update inventory stock",
        adminLabel: "a project admin",
    },
};

const todayISO = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const unwrapList = (response) => {
    const data = response?.data ?? response?.Data ?? response?.results ?? response;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.purchase_orders)) return data.purchase_orders;
    if (Array.isArray(data?.vendors)) return data.vendors;
    if (Array.isArray(data?.payments)) return data.payments;
    if (Array.isArray(data?.items)) return data.items;
    const firstArrayValue =
        data && typeof data === "object"
            ? Object.values(data).find((value) => Array.isArray(value))
            : null;
    if (Array.isArray(firstArrayValue)) return firstArrayValue;
    return [];
};

const unwrapObject = (response) => {
    const data = response?.data ?? response?.Data ?? response;
    if (Array.isArray(data)) return data[0] || {};
    if (data && typeof data === "object") return data;
    return {};
};

const mapPurchaseOrders = (purchaseOrders) => {
    return purchaseOrders.map((po) => ({
        ...po,
        total_price: po.lines?.reduce((sum, line) => sum + (line.total_price ?? 0), 0) ?? po.total_price ?? 0,
    }));
};

const getToken = () => localStorage.getItem("access") || "";

const getId = (item, keys = ["id"]) => {
    for (const key of keys) {
        if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== "") {
            return item[key];
        }
    }
    return null;
};

const isCompletedStatus = (status) => String(status || "").toLowerCase().includes("complete");

const isOngoingStatus = (status) => {
    const normalized = String(status || "").toLowerCase();
    return normalized.includes("ongoing") || normalized.includes("in progress") || normalized.includes("active");
};

const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const formatCurrency = (value) => `₹${toNumber(value).toLocaleString("en-IN")}`;

const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
};

const formatTodayLabel = () =>
    new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

const isSameLocalDay = (value, isoDate = todayISO()) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const [year, month, day] = isoDate.split("-").map(Number);
    return date.getFullYear() === year && date.getMonth() + 1 === month && date.getDate() === day;
};

const getAttachmentUrl = (update) => update?.file || update?.file_url || update?.attachment || "";

const fileNameFromUrl = (url, fallback = "Attachment") => {
    if (!url) return fallback;
    const cleanUrl = String(url).split("?")[0];
    const name = cleanUrl.split("/").pop();
    return name || fallback;
};

const isImageUrl = (url) => /\.(jpe?g|png|gif|webp|svg)$/i.test(String(url || "").split("?")[0]);

const getUpdateId = (response) => response?.data?.update_id || null;

const getReminderCompleted = (item) => Boolean(item?.is_completed || item?.progress?.is_complete);

const safeDate = (value) => {
    if (!value) return null;
    const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
};

const isTodayWithin = (start, end) => {
    const today = safeDate(todayISO());
    const startDate = safeDate(start);
    const endDate = safeDate(end);
    return Boolean(today && startDate && endDate && startDate <= today && today <= endDate);
};

const getReminderSiteName = (reminder) =>
    String(reminder?.site_name || reminder?.project || "").trim().toLowerCase();

const isAccessEnabled = (value) =>
    value === true || value === 1 || String(value).trim().toLowerCase() === "true";

const hasRequiredAccess = (permissions, requirement) =>
    Boolean(
        isAccessEnabled(permissions?.is_admin) ||
            requirement?.permissions?.some((permission) => isAccessEnabled(permissions?.[permission]))
    );

const fetchJson = async (path, options = {}) => {
    const response = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers: {
            Authorization: `Bearer ${getToken()}`,
            ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
            ...(options.headers || {}),
        },
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
        throw new Error(data?.detail || data?.message || response.statusText);
    }
    return data;
};

const createTaskOrSubtaskUpdate = async ({ type, id, comment, file }) => {
    const formData = new FormData();
    formData.append("comment", comment);
    if (file) formData.append("file", file);

    const path =
        type === "TASK_UPDATE"
            ? `/task/tasks/${id}/updates/create/`
            : `/task/subtasks/${id}/updates/create/`;

    return fetchJson(path, {
        method: "POST",
        body: formData,
    });
};

const getOrganizationMemberAccess = (response) => unwrapList(response)[0] || {};

const getSitePermissionAccess = (response) => unwrapObject(response);

const fetchActionAccess = async ({ actionType, organizationId, siteId }) => {
    const requirement = ACTION_ACCESS_REQUIREMENTS[actionType];
    if (!requirement) {
        return { allowed: true, requirement: null, error: "" };
    }

    if (requirement.scope === "organization") {
        if (!organizationId) {
            return {
                allowed: false,
                requirement,
                error: "We could not identify the organization for this reminder.",
            };
        }
        const response = await fetchJson(`/org/organization/${organizationId}/get-current-member/`);
        const permissions = getOrganizationMemberAccess(response);
        return {
            allowed: hasRequiredAccess(permissions, requirement),
            requirement,
            error: "",
        };
    }

    if (!siteId) {
        return { allowed: true, requirement, error: "" };
    }

    const response = await fetchJson("/team/get-member-permission/", {
        method: "POST",
        body: JSON.stringify({ sites: String(siteId) }),
    });
    const permissions = getSitePermissionAccess(response);
    return {
        allowed: hasRequiredAccess(permissions, requirement),
        requirement,
        error: "",
    };
};

const requestSubtaskApproval = async (updateId) =>
    fetchJson(`/task/subtasks/updates/${updateId}/request-approval/`, {
        method: "POST",
        body: JSON.stringify({ comment: "Requesting approval" }),
    });

const fieldClass =
    "w-full rounded-lg border border-gray-200 bg-white py-2 px-3 text-sm outline-none transition focus:border-[#3B0069] focus:ring-2 focus:ring-[#3B0069]/10 disabled:bg-gray-100 disabled:text-gray-400";

const Field = ({ label, children }) => (
    <label className="block">
        <span className="mb-1 block text-sm font-semibold text-gray-700">{label}</span>
        {children}
    </label>
);

const SectionTitle = ({ children, action }) => (
    <div className="mb-1 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold uppercase text-gray-500">{children}</h3>
        {action}
    </div>
);

const EmptyState = ({ children }) => (
    <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500">
        {children}
    </div>
);

const AccessBanner = ({ requirement, error, siteName, onClose }) => {
    const scopeLabel = requirement?.scope === "site" && siteName ? ` for ${siteName}` : "";
    const title = error ? "Access could not be verified" : `${requirement?.permissionLabel || "Access"} required`;
    const description =
        error ||
        `You can view this reminder, but you cannot ${requirement.actionLabel}${scopeLabel} from this drawer. Ask ${requirement.adminLabel} to grant ${requirement.permissionLabel}, or have an admin complete this action.`;

    return (
        <>
            <div className="space-y-4">
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900 shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
                            <ShieldAlert className="size-5" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold text-amber-950">{title}</h3>
                            <p className="mt-1 text-sm leading-6 text-amber-800">{description}</p>
                            {!error && (
                                <div className="mt-3 rounded-lg border border-amber-200 bg-white/70 px-3 py-2 text-xs font-semibold text-amber-900">
                                    Required access: {requirement.permissionLabel} or Admin
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <SubmitBar onCancel={onClose} onSubmit={onClose} submitting={false} submitLabel="Close" hideCancel />
        </>
    );
};

const AccessLoadingState = ({ onClose }) => (
    <>
        <EmptyState>
            <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                Checking your access...
            </span>
        </EmptyState>
        <SubmitBar onCancel={onClose} onSubmit={onClose} submitting={false} submitLabel="Close" hideCancel />
    </>
);

const useReminderActionAccess = ({ actionType, organizationId, siteId }) => {
    const requirement = ACTION_ACCESS_REQUIREMENTS[actionType] || null;
    const [state, setState] = useState({
        allowed: true,
        loading: false,
        error: "",
        requirement: null,
    });

    useEffect(() => {
        let cancelled = false;

        if (!requirement) {
            setState({ allowed: true, loading: false, error: "", requirement: null });
            return () => {
                cancelled = true;
            };
        }

        setState({ allowed: false, loading: true, error: "", requirement });
        fetchActionAccess({ actionType, organizationId, siteId })
            .then((result) => {
                if (!cancelled) setState({ loading: false, ...result });
            })
            .catch((error) => {
                console.error("Failed to verify reminder action access", error);
                if (!cancelled) {
                    setState({
                        allowed: false,
                        loading: false,
                        error: error.message || "Please try again, or ask an admin to confirm your access.",
                        requirement,
                    });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [actionType, organizationId, requirement, siteId]);

    return state;
};

const AttachmentPicker = ({ label = "Attachment", file, previewUrl, onChange, onRemove, accept }) => (
    <Field label={label}>
        <div className="rounded-xl border border-gray-100 bg-white p-4">
            {!file && (
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md bg-purple-50 p-2.5 text-sm font-semibold text-purple-600 transition hover:bg-purple-100">
                    <Upload className="size-4" />
                    Upload Photo
                    <input
                        type="file"
                        accept={accept}
                        className="hidden"
                        onChange={(event) => onChange(event.target.files?.[0] || null)}
                    />
                </label>
            )}
            {file && (
                <div className="mt-1">
                    {previewUrl ? (
                        <div className="relative h-48 w-full overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                            <img src={previewUrl} alt="Receipt" className="h-full w-full rounded-md object-contain" />
                            <button
                                type="button"
                                onClick={onRemove}
                                className="absolute right-2 top-2 rounded-full bg-red-500 p-1 text-white"
                            >
                                <X className="size-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                            <span className="flex min-w-0 items-center gap-2 text-sm text-gray-700">
                                <FileText className="size-4 shrink-0 text-gray-500" />
                                <span className="truncate">{file.name}</span>
                            </span>
                            <button type="button" onClick={onRemove} className="rounded-full p-1 text-gray-500 hover:bg-gray-200">
                                <X className="size-4" />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    </Field>
);

const SubmitBar = ({
    children,
    onCancel,
    onSubmit,
    disabled,
    submitting,
    submitLabel = "Save",
    cancelLabel = "Cancel",
    hideCancel = false,
}) => (
    <div className="fixed bottom-0 right-0 z-20 flex w-full items-center justify-end gap-2 border-l border-t border-gray-200 bg-white px-4 py-4 shadow-[0_-8px_20px_rgba(15,23,42,0.08)] sm:gap-3 sm:px-6 md:w-[520px] xl:w-[600px]">
        {!hideCancel && (
            <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                className="whitespace-nowrap rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 disabled:opacity-50 sm:px-5"
            >
                {cancelLabel}
            </button>
        )}
        {children}
        <button
            type="button"
            onClick={onSubmit}
            disabled={disabled || submitting}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-[#3B0069] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2d0050] disabled:cursor-not-allowed disabled:opacity-50 sm:px-6"
        >
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {submitting ? "Saving..." : submitLabel}
        </button>
    </div>
);

const DrawerShell = ({
    title,
    subtitle,
    onClose,
    children,
    currentIndex = 0,
    total = 1,
    onPrevious,
    onNext,
    showNavigation = true,
}) => (
    <div className="fixed inset-0 z-50 flex bg-black/40" onClick={onClose}>
        <div
            className="ml-auto flex h-full w-full flex-col border-l border-gray-200 bg-[#FAFBFD] shadow-xl md:w-[520px] xl:w-[600px]"
            onClick={(event) => event.stopPropagation()}
        >
            <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-3">
                <div className="mb-4 flex gap-1.5">
                    {Array.from({ length: Math.max(1, total) }).map((_, index) => (
                        <div key={index} className="h-1 flex-1 overflow-hidden rounded-full bg-gray-200">
                            <div
                                className={`h-full rounded-full transition-all ${
                                    index === currentIndex
                                        ? "bg-[#3B0069]"
                                        : index < currentIndex
                                          ? "bg-emerald-500"
                                          : "bg-gray-200"
                                }`}
                                style={{ width: index <= currentIndex ? "100%" : "0%" }}
                            />
                        </div>
                    ))}
                </div>
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-gray-400">
                            <span>Action {currentIndex + 1} of {Math.max(1, total)}</span>
                        </div>
                        <h2 className="truncate text-xl font-semibold text-gray-900">{title}</h2>
                        {subtitle && <p className="mt-1 truncate text-sm text-gray-500">{subtitle}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                        {showNavigation && (
                            <>
                                <button
                                    type="button"
                                    onClick={onPrevious}
                                    disabled={!onPrevious || currentIndex === 0}
                                    className="rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                                >
                                    <ChevronLeft className="size-5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={onNext}
                                    disabled={!onNext || currentIndex >= total - 1}
                                    className="rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                                >
                                    <ChevronRight className="size-5" />
                                </button>
                            </>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                        >
                            <X className="size-5" />
                        </button>
                    </div>
                </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-28 pt-3">{children}</div>
        </div>
    </div>
);

const useSiteGuard = (siteId) => {
    const normalized = Number(siteId);
    return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
};

function InventoryUpdateForm({ reminder, onClose, onSaved }) {
    const siteId = useSiteGuard(reminder?.site);
    const { addAlert } = useAlert();
    const [materials, setMaterials] = useState([]);
    const [boqItems, setBoqItems] = useState([]);
    const [selected, setSelected] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [taskByMaterial, setTaskByMaterial] = useState({});
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [updateDate, setUpdateDate] = useState(todayISO());

    useEffect(() => {
        if (!siteId) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([
            MaterialsApi.listAllMaterialsBySite({ sites: siteId }),
            getLinkedTasksBySite(siteId).catch(() => []),
        ])
            .then(([materialResponse, linkedTasks]) => {
                if (cancelled) return;
                setMaterials(unwrapList(materialResponse));
                setBoqItems(
                    unwrapList(linkedTasks).filter(
                        (item) => !isCompletedStatus(item?.task_status || item?.status)
                    )
                );
            })
            .catch((error) => {
                console.error("Failed to load inventory reminder data", error);
                // addAlert("Failed to load site materials", "error");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [addAlert, siteId]);

    const filteredMaterials = useMemo(() => {
        const value = query.trim().toLowerCase();
        if (!value) return materials;
        return materials.filter((material) =>
            [material?.material_name, material?.subcategory, material?.specification, material?.uom]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()
                .includes(value)
        );
    }, [materials, query]);

    const toggleMaterial = (id) => {
        setSelected((current) =>
            current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
        );
    };

    const handleSubmit = async () => {
        if (!siteId) return;
        if (!selected.length) {
            addAlert("Please select at least one material", "error");
            return;
        }

        const selectedMaterials = materials.filter((material) => selected.includes(material.id));
        const invalid = selectedMaterials.find((material) => toNumber(quantities[material.id]) <= 0);
        if (invalid) {
            addAlert(`Enter a valid quantity for ${invalid.material_name}`, "error");
            return;
        }

        const overused = selectedMaterials.find(
            (material) => toNumber(quantities[material.id]) > toNumber(material.current_stock)
        );
        if (overused) {
            addAlert(`Cannot use more than available stock for ${overused.material_name}`, "error");
            return;
        }
        const missingTask = selectedMaterials.find((material) => !taskByMaterial[material.id]);
        // if (missingTask) {
        //     addAlert(`Please link a task for ${missingTask.material_name}`, "error");
        //     return;
        // }

        setSubmitting(true);
        try {
            await Promise.all(
                selectedMaterials.map((material) =>
                    fetch(`${BASE_URL}/inventory/create-stock/`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${getToken()}`,
                        },
                        body: JSON.stringify({
                            update_date: updateDate,
                            entered_quantity: -toNumber(quantities[material.id]),
                            comment: "",
                            material: material.id,
                            task_id: taskByMaterial[material.id] || null,
                        }),
                    }).then(async (response) => {
                        if (!response.ok) {
                            const data = await response.json().catch(() => null);
                            throw new Error(data?.message || `Failed to update ${material.material_name}`);
                        }
                    })
                )
            );
            addAlert("Stock updated successfully", "success");
            onSaved();
        } catch (error) {
            console.error("Stock update reminder failed", error);
            addAlert(error.message || "Failed to update stock", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="space-y-4">
                {!siteId && <EmptyState>This reminder is missing a site.</EmptyState>}
                {siteId && (
                    <>
                        <div className="grid gap-3 rounded-xl border border-gray-200 bg-white p-4">
                            <div className="rounded-lg border border-[#3B0069]/20 bg-[#ECE6F0] px-3 py-2 text-sm font-semibold text-[#3B0069]">
                                Use from inventory
                            </div>
                            <Field label="Update date">
                                <input
                                    type="date"
                                    max={todayISO()}
                                    value={updateDate}
                                    onChange={(event) => setUpdateDate(event.target.value)}
                                    className={fieldClass}
                                />
                            </Field>
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder="Search materials"
                                    className={`w-full rounded-lg border border-gray-200 bg-white py-2 text-sm outline-none transition focus:border-[#3B0069] focus:ring-2 focus:ring-[#3B0069]/10 disabled:bg-gray-100 disabled:text-gray-400 pl-9`}
                                />
                            </div>
                        </div>

                        {loading ? (
                            <EmptyState>Loading materials...</EmptyState>
                        ) : filteredMaterials.length ? (
                            <div className="space-y-3">
                                {filteredMaterials.map((material) => {
                                    const isSelected = selected.includes(material.id);
                                    return (
                                        <div
                                            key={material.id}
                                            className={`rounded-xl border p-3.5 transition ${
                                                isSelected
                                                    ? "border-[#3B0069] bg-[#F7F2FB]"
                                                    : "border-gray-200 bg-white"
                                            }`}
                                        >
                                            <div className="grid grid-cols-[24px_minmax(0,1fr)_110px_auto] items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleMaterial(material.id)}
                                                    className="size-4 rounded border-gray-300"
                                                />
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-semibold text-gray-900">
                                                        {material.material_name}
                                                    </p>
                                                    <p className="truncate text-xs text-gray-500">
                                                        In stock: {material.current_stock || 0} {material.uom}
                                                    </p>
                                                    {(material.last_updated_on || material.last_updated_by || material.last_stock !== undefined) && (
                                                        <p className="mt-0.5 truncate text-[11px] text-gray-400">
                                                            {material.last_stock !== undefined && material.last_stock !== null
                                                                ? `Last stock: ${toNumber(material.last_stock).toLocaleString("en-IN")} ${material.uom || ""}`
                                                                : ""}
                                                            {material.last_updated_by ? `${material.last_stock !== undefined && material.last_stock !== null ? " · " : ""}By ${material.last_updated_by}` : ""}
                                                            {material.last_updated_on ? ` · ${formatDateTime(material.last_updated_on)}` : ""}
                                                        </p>
                                                    )}
                                                </div>
                                                <input
                                                    type="text"
                                                    min="0"
                                                    step="1"
                                                    disabled={!isSelected || submitting}
                                                    value={quantities[material.id] || ""}
                                                    onChange={(event) =>{
                                                        // Allow only numbers in the input
                                                        event.target.value = event.target.value.replace(/[^0-9]/g, "");

                                                        setQuantities((current) => ({
                                                            ...current,
                                                            [material.id]: event.target.value,
                                                        }))
                                                    }
                                                    }
                                                    placeholder="Qty"
                                                    className={`${fieldClass} text-center`}
                                                />
                                                <span className="text-xs font-semibold text-gray-500">{material.uom}</span>
                                            </div>
                                            {isSelected && (
                                                <select
                                                    value={taskByMaterial[material.id] || ""}
                                                    onChange={(event) =>
                                                        setTaskByMaterial((current) => ({
                                                            ...current,
                                                            [material.id]: event.target.value,
                                                        }))
                                                    }
                                                    className={`${fieldClass} mt-3`}
                                                >
                                                    <option value="">Link to task</option>
                                                    {boqItems.map((item) => (
                                                        <option key={`${item.id}-${item.task}`} value={item.task}>
                                                            {item.task_name || item.item_name} · {item.sectionName}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptyState>No materials found.</EmptyState>
                        )}
                    </>
                )}
            </div>
            <SubmitBar
                onCancel={onClose}
                onSubmit={handleSubmit}
                disabled={!siteId || !selected.length}
                submitting={submitting}
            />
        </>
    );
}

function TaskUpdateForm({ reminder, type, onClose, onSaved }) {
    const siteId = useSiteGuard(reminder?.site);
    const { addAlert } = useAlert();
    const [items, setItems] = useState([]);
    const [index, setIndex] = useState(0);
    const [drafts, setDrafts] = useState({});
    const [reviewing, setReviewing] = useState(false);
    const [todayUpdates, setTodayUpdates] = useState([]);
    const [loadingUpdates, setLoadingUpdates] = useState(false);
    const [filePreviews, setFilePreviews] = useState({});
    const filePreviewsRef = useRef({});
    const [viewerFile, setViewerFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!siteId) return;
        let cancelled = false;
        setLoading(true);
        (async () => {
            if (type === "SUBTASK_UPDATE") {
                const subtasks = unwrapList(await fetchJson(`/task/subtasks-by-site/${siteId}/`));
                return subtasks.filter(
                    (subtask) =>
                        !subtask.is_completed &&
                        !isCompletedStatus(subtask.status) &&
                        isTodayWithin(subtask.start_date, subtask.end_date)
                );
            }

            const groups = unwrapList(await taskApi.getAllTaskGroupsBySite(siteId));
            const tasksByGroup = await Promise.all(
                groups.map(async (group) => {
                    const tasks = unwrapList(await taskApi.getAllTasks(group.id));
                    return tasks.map((task) => ({ ...task, group_name: group.group_name || group.name }));
                })
            );
            const tasks = tasksByGroup.flat();

            if (type === "TASK_UPDATE") {
                return tasks.filter((task) => isOngoingStatus(task.task_status || task.status));
            }

            return [];
        })()
            .then((result) => {
                if (!cancelled) setItems(result);
            })
            .catch((error) => {
                console.error("Failed to load task reminder data", error);
                // addAlert("Failed to load tasks", "error");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [addAlert, siteId, type]);

    const getItemId = (item) =>
        getId(
            item,
            type === "TASK_UPDATE" ? ["task_id", "id"] : ["subtask_id", "id"]
        );
    const getItemTitle = (item) =>
        type === "TASK_UPDATE" ? item?.task_name || item?.name : item?.subtask_name || item?.name;
    const getItemMeta = (item) =>
        [
            item?.group_name,
            type === "SUBTASK_UPDATE" ? item?.task_name : null,
            item?.start_date && item?.end_date ? `${formatDateLong(item.start_date)} to ${formatDateLong(item.end_date)}` : null,
        ]
            .filter(Boolean)
            .join(" · ");

    const current = items[index];
    const currentId = current ? getItemId(current) : null;
    const currentDraft = currentId !== null ? drafts[currentId] || {} : {};
    const currentComment = currentDraft.comment || "";
    const currentFile = currentDraft.file || null;
    const currentFilePreview = currentId !== null ? filePreviews[currentId] || null : null;
    const requestApproval = Boolean(currentDraft.requestApproval);
    const enteredUpdates = items
        .map((item) => {
            const id = getItemId(item);
            return {
                item,
                id,
                draft: id !== null ? drafts[id] || {} : {},
            };
        })
        .filter(({ id, draft }) => id !== null && String(draft.comment || "").trim());
    const title = current ? getItemTitle(current) : "";
    const isLastItem = index >= items.length - 1;

    useEffect(() => {
        if (!currentId) {
            setTodayUpdates([]);
            return;
        }
        let cancelled = false;
        setLoadingUpdates(true);
        const path =
            type === "TASK_UPDATE"
                ? `/task/tasks/${currentId}/updates/`
                : `/task/subtasks/${currentId}/updates/`;
        fetchJson(path)
            .then((response) => {
                if (cancelled) return;
                const updates = unwrapList(response)
                    .filter((update) => isSameLocalDay(update?.created_on || update?.created_at || update?.date))
                    .reverse();
                setTodayUpdates(updates);
            })
            .catch((error) => {
                console.error("Failed to load today's updates", error);
                if (!cancelled) setTodayUpdates([]);
            })
            .finally(() => {
                if (!cancelled) setLoadingUpdates(false);
            });
        return () => {
            cancelled = true;
        };
    }, [currentId, type]);

    const updateDraft = (changes) => {
        if (currentId === null) return;
        setDrafts((currentDrafts) => ({
            ...currentDrafts,
            [currentId]: {
                ...(currentDrafts[currentId] || {}),
                ...changes,
                skipped: false,
            },
        }));
    };

    const updateDraftFile = (file) => {
        if (currentId === null) return;
        const previousPreview = filePreviewsRef.current[currentId];
        if (previousPreview) URL.revokeObjectURL(previousPreview);

        const nextPreview = file?.type?.startsWith("image/") ? URL.createObjectURL(file) : null;
        filePreviewsRef.current = {
            ...filePreviewsRef.current,
            [currentId]: nextPreview,
        };
        setFilePreviews(filePreviewsRef.current);
        updateDraft({ file });
    };

    const clearPreviewForId = (id) => {
        const previousPreview = filePreviewsRef.current[id];
        if (previousPreview) URL.revokeObjectURL(previousPreview);
        const nextPreviews = { ...filePreviewsRef.current };
        delete nextPreviews[id];
        filePreviewsRef.current = nextPreviews;
        setFilePreviews(nextPreviews);
    };

    useEffect(() => {
        return () => {
            Object.values(filePreviewsRef.current).forEach((url) => {
                if (url) URL.revokeObjectURL(url);
            });
        };
    }, []);

    const goNext = () => {
        if (isLastItem) {
            onSaved();
            return;
        }
        setIndex((value) => Math.min(items.length - 1, value + 1));
    };

    const handleSkip = () => {
        if (currentId !== null) {
            clearPreviewForId(currentId);
            setDrafts((currentDrafts) => ({
                ...currentDrafts,
                [currentId]: {
                    comment: "",
                    file: null,
                    skipped: true,
                },
            }));
        }
        goNext();
    };

    const handleSaveStep = async () => {
        if (!current || !currentComment.trim()) {
            addAlert("Write an update before continuing", "error");
            return;
        }
        setSubmitting(true);
        try {
            const response = await createTaskOrSubtaskUpdate({
                type,
                id: currentId,
                comment: currentComment.trim(),
                file: currentFile,
            });
            if (response?.status === false || response?.Status === false) {
                throw new Error(response?.message || "Failed to post update");
            }
            const updateId = getUpdateId(response);
            if (type === "SUBTASK_UPDATE" && requestApproval && updateId) {
                await requestSubtaskApproval(updateId);
            }
            addAlert(requestApproval ? "Update posted and sent for approval" : "Update posted successfully", "success");
            setDrafts((currentDrafts) => {
                const next = { ...currentDrafts };
                delete next[currentId];
                return next;
            });
            clearPreviewForId(currentId);
            goNext();
        } catch (error) {
            console.error("Reminder task update failed", error);
            addAlert(error.message || "Failed to post update", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        if (!enteredUpdates.length) {
            addAlert("Enter at least one update before saving", "error");
            return;
        }
        setSubmitting(true);
        try {
            await Promise.all(
                enteredUpdates.map(async ({ id, draft }) => {
                    const response = await createTaskOrSubtaskUpdate({
                        type,
                        id,
                        comment: draft.comment.trim(),
                        file: draft.file || null,
                    });
                    if (response?.status === false || response?.Status === false) {
                        throw new Error(response?.message || "Failed to post update");
                    }
                })
            );
            addAlert(
                enteredUpdates.length === 1 ? "Update posted successfully" : "Updates posted successfully",
                "success"
            );
            onSaved();
        } catch (error) {
            console.error("Reminder task update failed", error);
            addAlert(error.message || "Failed to post updates", "error");
        } finally {
            setSubmitting(false);
        }
    };

    if (reviewing) {
        return (
            <>
                <div className="space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white p-4">
                        <SectionTitle
                            action={
                                <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                                    {enteredUpdates.length} ready
                                </span>
                            }
                        >
                            Review updates
                        </SectionTitle>
                        <p className="text-sm text-gray-500">
                            Check the updates below before saving them.
                        </p>
                    </div>
                    {items.map((item, itemIndex) => {
                        const id = getItemId(item);
                        const draft = id !== null ? drafts[id] || {} : {};
                        const commentText = String(draft.comment || "").trim();
                        return (
                            <div key={id || itemIndex} className="rounded-xl border border-gray-200 bg-white p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="mb-1 flex items-center gap-2">
                                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
                                                {itemIndex + 1}/{items.length}
                                            </span>
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                    commentText
                                                        ? "bg-emerald-50 text-emerald-700"
                                                        : "bg-gray-100 text-gray-500"
                                                }`}
                                            >
                                                {commentText ? "Update entered" : "Skipped"}
                                            </span>
                                        </div>
                                        <h3 className="truncate text-base font-semibold text-gray-900">
                                            {getItemTitle(item)}
                                        </h3>
                                        {getItemMeta(item) && (
                                            <p className="mt-1 truncate text-sm text-gray-500">{getItemMeta(item)}</p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIndex(itemIndex);
                                            setReviewing(false);
                                        }}
                                        className="shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                                    >
                                        Edit
                                    </button>
                                </div>
                                {commentText ? (
                                    <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                                        <p className="whitespace-pre-wrap">{commentText}</p>
                                        {draft.file && (
                                            <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-gray-500">
                                                <Paperclip className="size-3.5" />
                                                <span className="truncate">{draft.file.name}</span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="mt-3 text-sm text-gray-500">No update will be saved for this item.</p>
                                )}
                            </div>
                        );
                    })}
                </div>
                <SubmitBar
                    onCancel={() => setReviewing(false)}
                    onSubmit={handleSubmit}
                    disabled={!enteredUpdates.length}
                    submitting={submitting}
                    cancelLabel="Back"
                    submitLabel="Save"
                />
            </>
        );
    }

    return (
        <>
            <div className="space-y-4">
                {!siteId && <EmptyState>This reminder is missing a site.</EmptyState>}
                {siteId && loading && <EmptyState>Loading work...</EmptyState>}
                {siteId && !loading && !items.length && (
                    <EmptyState>
                        {type === "TASK_UPDATE"
                            ? "No ongoing tasks found for this site."
                            : "No scheduled subtasks are active today."}
                    </EmptyState>
                )}
                {current && (
                    <>
                        <Field label={type === "TASK_UPDATE" ? "Switch task" : "Switch subtask"}>
                            <select
                                value={currentId || ""}
                                onChange={(event) => {
                                    const nextIndex = items.findIndex((item) => String(getItemId(item)) === String(event.target.value));
                                    if (nextIndex >= 0) setIndex(nextIndex);
                                }}
                                className={fieldClass}
                            >
                                {items.map((item, itemIndex) => {
                                    const id = getItemId(item);
                                    return (
                                        <option key={id || itemIndex} value={id || ""}>
                                            {itemIndex + 1}. {getItemTitle(item)}
                                        </option>
                                    );
                                })}
                            </select>
                        </Field>
                        <div className="rounded-lg border border-gray-200 bg-white p-3">
                            <SectionTitle
                                action={
                                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                                        {index + 1}/{items.length}
                                    </span>
                                }
                            >
                                {type === "TASK_UPDATE" ? "Ongoing task" : "Active subtask"}
                            </SectionTitle>
                            <h3 className="text-base font-semibold text-gray-900">{title}</h3>
                            {getItemMeta(current) && (
                                <p className="mt-0 text-sm text-gray-500">{getItemMeta(current)}</p>
                            )}
                        </div>
                        <Field label="Post update">
                            <textarea
                                value={currentComment}
                                onChange={(event) => updateDraft({ comment: event.target.value })}
                                placeholder="What changed today?"
                                className={`${fieldClass} min-h-[140px] resize-none`}
                            />
                        </Field>
                        <AttachmentPicker
                            label="Attachment"
                            file={currentFile}
                            previewUrl={currentFilePreview}
                            onChange={updateDraftFile}
                            onRemove={() => updateDraftFile(null)}
                        />
                        {type === "SUBTASK_UPDATE" && (
                            <label className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                <span>
                                    <span className="block font-semibold">Send for approval</span>
                                    <span className="text-xs text-amber-700">Request approval after this update is posted.</span>
                                </span>
                                <input
                                    type="checkbox"
                                    checked={requestApproval}
                                    onChange={(event) => updateDraft({ requestApproval: event.target.checked })}
                                    className="size-4"
                                />
                            </label>
                        )}
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <SectionTitle
                                action={
                                    <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                                        Today
                                    </span>
                                }
                            >
                                Updates for {formatTodayLabel()}
                            </SectionTitle>
                            {loadingUpdates ? (
                                <p className="text-sm text-gray-500">Loading updates...</p>
                            ) : todayUpdates.length ? (
                                <div className="relative space-y-4">
                                    <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" />
                                    {todayUpdates.map((update) => {
                                        const fileUrl = getAttachmentUrl(update);
                                        const name = String(update?.created_by || "User").split("|").pop();
                                        const approvalStatus =
                                            update?.approval_status || update?.status || (update?.is_request_for_approval ? "Pending approval" : "");
                                        return (
                                            <div key={update.id || `${name}-${update.created_on}`} className="relative z-10 flex gap-3">
                                                <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-[#343C6A] text-xs font-semibold text-white">
                                                    {String(name || "U").trim().slice(0, 1).toUpperCase()}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                                        <span className="text-sm font-bold text-gray-900">{name}</span>
                                                        <span className="text-xs text-gray-400">{formatDateTime(update.created_on)}</span>
                                                        {type === "SUBTASK_UPDATE" && approvalStatus && (
                                                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700">
                                                                {approvalStatus}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {update.comment && (
                                                        <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{update.comment}</p>
                                                    )}
                                                    {fileUrl && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setViewerFile({
                                                                    url: fileUrl,
                                                                    name: fileNameFromUrl(fileUrl),
                                                                })
                                                            }
                                                            className="mt-2 block max-w-full text-left"
                                                        >
                                                            {isImageUrl(fileUrl) ? (
                                                                <img
                                                                    src={fileUrl}
                                                                    alt="Attachment"
                                                                    className="max-h-28 max-w-full rounded-lg border border-gray-200 object-contain transition hover:opacity-90"
                                                                />
                                                            ) : (
                                                                <span className="inline-flex max-w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100">
                                                                    <Paperclip className="size-3.5 shrink-0" />
                                                                    <span className="truncate">{fileNameFromUrl(fileUrl)}</span>
                                                                </span>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No updates posted today.</p>
                            )}
                        </div>
                    </>
                )}
            </div>
            <SubmitBar
                onCancel={onClose}
                onSubmit={handleSaveStep}
                disabled={!current || !currentComment.trim()}
                submitting={submitting}
                submitLabel={isLastItem ? "Save" : "Save & Next"}
            >
                <button
                    type="button"
                    onClick={handleSkip}
                    disabled={!current || submitting}
                    className="whitespace-nowrap rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 sm:px-5"
                >
                    Skip
                </button>
            </SubmitBar>
            {viewerFile && (
                <FileViewer
                    fileUrl={viewerFile.url}
                    fileName={viewerFile.name}
                    onClose={() => setViewerFile(null)}
                />
            )}
        </>
    );
}

function WorkerAttendanceForm({ reminder, onClose, onSaved }) {
    const siteId = useSiteGuard(reminder?.site);
    const { addAlert } = useAlert();
    const [workers, setWorkers] = useState([]);
    const [selected, setSelected] = useState([]);
    const [attendance, setAttendance] = useState({});
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!siteId) return;
        let cancelled = false;
        setLoading(true);
        fetch(`${BASE_URL}/worker/workers-page/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify({ worker_type: "Worker", sites: siteId }),
        })
            .then((response) => response.json())
            .then((data) => {
                if (cancelled) return;
                const list = unwrapList(data).filter(
                    (worker) => String(worker?.worker_type || "Worker").toLowerCase() === "worker"
                );
                setWorkers(list);
                const defaults = {};
                list.forEach((worker) => {
                    const id = getId(worker, ["worker_id", "id"]);
                    const status = String(worker?.attendance_status || "").toLowerCase() === "absent" ? "Absent" : "Present";
                    const hasOvertime = toNumber(worker?.overtime_hours) > 0;
                    defaults[id] = {
                        attendance_status: status,
                        dayType: status === "Absent" ? "Full day" : hasOvertime ? "Overtime" : worker?.half_day ? "Half day" : "Full day",
                        overtime_hours: hasOvertime ? toNumber(worker?.overtime_hours) : 0,
                        overtime_wage: hasOvertime ? toNumber(worker?.overtime_wage) : 0,
                    };
                });
                setAttendance(defaults);
            })
            .catch((error) => {
                console.error("Failed to load workers", error);
                // addAlert("Failed to load workers", "error");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [addAlert, siteId]);

    const filteredWorkers = workers.filter((worker) =>
        String(worker.worker_name || worker.name || "").toLowerCase().includes(query.trim().toLowerCase())
    );
    const allFilteredSelected =
        filteredWorkers.length > 0 &&
        filteredWorkers.every((worker) => selected.includes(getId(worker, ["worker_id", "id"])));

    const updateAttendance = (id, field, value) => {
        setAttendance((current) => {
            const worker = workers.find((item) => String(getId(item, ["worker_id", "id"])) === String(id));
            const dailyWage = toNumber(worker?.daily_wage);
            const next = {
                ...(current[id] || {}),
                [field]: value,
            };

            if (field === "dayType" && value !== "Overtime") {
                next.overtime_hours = 0;
                next.overtime_wage = 0;
            }

            if (field === "dayType" && value === "Overtime") {
                const hours = Math.min(12, Math.max(0, toNumber(next.overtime_hours)));
                next.overtime_hours = hours;
                next.overtime_wage = Math.round((dailyWage / 8) * hours);
            }

            if (field === "overtime_hours") {
                const hours = Math.min(12, Math.max(0, toNumber(value)));
                next.overtime_hours = hours;
                next.overtime_wage = Math.round((dailyWage / 8) * hours);
            }

            if (field === "attendance_status" && value === "Absent") {
                next.dayType = "Full day";
                next.overtime_hours = 0;
                next.overtime_wage = 0;
            }

            return {
                ...current,
                [id]: next,
            };
        });
    };

    const buildBody = (id) => {
        const item = attendance[id] || {};
        if (item.attendance_status === "Absent") {
            return {
                attendance_status: "Absent",
                half_day: "False",
                full_day: "False",
                overtime_hours: 0,
                overtime_wage: 0,
                persion_count: 1,
            };
        }
        return {
            attendance_status: "Present",
            half_day: item.dayType === "Half day" ? "True" : "False",
            full_day: item.dayType === "Half day" ? "False" : "True",
            overtime_hours: item.dayType === "Overtime" ? toNumber(item.overtime_hours) : 0,
            overtime_wage: item.dayType === "Overtime" ? toNumber(item.overtime_wage) : 0,
            persion_count: 1,
        };
    };

    const handleSubmit = async () => {
        if (!selected.length) {
            addAlert("Select at least one worker", "error");
            return;
        }
        setSubmitting(true);
        try {
            await Promise.all(
                selected.map((id) =>
                    fetch(`${BASE_URL}/worker/mark-worker-attendance/${id}/`, {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${getToken()}`,
                        },
                        body: JSON.stringify(buildBody(id)),
                    }).then((response) => {
                        if (!response.ok) throw new Error("Failed to mark attendance");
                    })
                )
            );
            addAlert("Worker attendance marked", "success");
            onSaved();
        } catch (error) {
            console.error("Worker attendance reminder failed", error);
            addAlert(error.message || "Failed to mark attendance", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="space-y-4">
                {!siteId && <EmptyState>This reminder is missing a site.</EmptyState>}
                {siteId && (
                    <>
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <div className="mb-3 rounded-lg border border-[#3B0069]/20 bg-[#ECE6F0] px-3 py-2 text-sm font-semibold text-[#3B0069]">
                                Marking workers for today · {formatTodayLabel()}
                            </div>
                            <div className="flex items-center justify-between gap-3">
                                <label className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        onChange={() => {
                                            const ids = filteredWorkers.map((worker) => getId(worker, ["worker_id", "id"]));
                                            setSelected((current) =>
                                                allFilteredSelected
                                                    ? current.filter((id) => !ids.includes(id))
                                                    : Array.from(new Set([...current, ...ids]))
                                            );
                                        }}
                                    />
                                    Select all
                                </label>
                                <div className="relative flex-1">
                                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        value={query}
                                        onChange={(event) => setQuery(event.target.value)}
                                        placeholder="Search workers"
                                        className={`w-full rounded-lg border border-gray-200 bg-white py-2 text-sm outline-none transition focus:border-[#3B0069] focus:ring-2 focus:ring-[#3B0069]/10 disabled:bg-gray-100 disabled:text-gray-400 pl-9`}
                                    />
                                </div>
                            </div>
                        </div>
                        {loading ? (
                            <EmptyState>Loading workers...</EmptyState>
                        ) : filteredWorkers.length ? (
                            <div className="space-y-3">
                                {filteredWorkers.map((worker) => {
                                    const id = getId(worker, ["worker_id", "id"]);
                                    const data = attendance[id] || {};
                                    return (
                                        <div key={id} className="rounded-xl border border-gray-200 bg-white p-3.5">
                                            <div className="grid grid-cols-[24px_minmax(0,1fr)_120px] items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.includes(id)}
                                                    onChange={() =>
                                                        setSelected((current) =>
                                                            current.includes(id)
                                                                ? current.filter((item) => item !== id)
                                                                : [...current, id]
                                                        )
                                                    }
                                                />
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-semibold text-gray-900">
                                                        {worker.worker_name || worker.name}
                                                    </p>
                                                    <p className="truncate text-xs text-gray-500">
                                                        {worker.worker_workname || worker.worker_workcategory || "Worker"}
                                                    </p>
                                                </div>
                                                <select
                                                    value={data.attendance_status || "Present"}
                                                    onChange={(event) => updateAttendance(id, "attendance_status", event.target.value)}
                                                    className={fieldClass}
                                                >
                                                    <option>Present</option>
                                                    <option>Absent</option>
                                                </select>
                                            </div>
                                            {data.attendance_status !== "Absent" && (
                                                <div className={`mt-3 grid gap-2 ${data.dayType === "Overtime" ? "grid-cols-3" : "grid-cols-1"}`}>
                                                    <select
                                                        value={data.dayType || "Full day"}
                                                        onChange={(event) => updateAttendance(id, "dayType", event.target.value)}
                                                        className={fieldClass}
                                                    >
                                                        <option>Full day</option>
                                                        <option>Half day</option>
                                                        <option>Overtime</option>
                                                    </select>
                                                    {data.dayType === "Overtime" && (
                                                        <>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="12"
                                                                value={data.overtime_hours || ""}
                                                                onChange={(event) => updateAttendance(id, "overtime_hours", event.target.value)}
                                                                placeholder="OT hours"
                                                                className={fieldClass}
                                                            />
                                                            <div className="relative">
                                                                <IndianRupee className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                                                                <input
                                                                    type="number"
                                                                    readOnly
                                                                    value={data.overtime_wage || 0}
                                                                    placeholder="OT wage"
                                                                    className={`w-full rounded-lg border border-gray-200 bg-white py-2 text-sm outline-none transition focus:border-[#3B0069] focus:ring-2 focus:ring-[#3B0069]/10 disabled:bg-gray-100 disabled:text-gray-400 pl-9`}
                                                                />
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptyState>No workers found.</EmptyState>
                        )}
                    </>
                )}
            </div>
            <SubmitBar
                onCancel={onClose}
                onSubmit={handleSubmit}
                disabled={!siteId || !selected.length}
                submitting={submitting}
                submitLabel="Mark attendance"
            />
        </>
    );
}

function ContractorAttendanceForm({ reminder, organizationId, onClose, onSaved }) {
    const siteId = useSiteGuard(reminder?.site);
    const { addAlert } = useAlert();
    const [rows, setRows] = useState([]);
    const [selectedWorkOrder, setSelectedWorkOrder] = useState("");
    const [details, setDetails] = useState(null);
    const [form, setForm] = useState({
        attendance_status: "Present",
        person_count: 1,
        hours: 8,
        scope_items: [],
    });
    const [loading, setLoading] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!organizationId || !siteId) return;
        let cancelled = false;
        setLoading(true);
        fetch(`${BASE_URL}/org/contractor/attendance/today/${organizationId}/`, {
            headers: { Authorization: `Bearer ${getToken()}` },
        })
            .then((response) => response.json())
            .then((data) => {
                if (cancelled) return;
                const list = unwrapList(data).filter((row) => String(row.site) === String(siteId));
                setRows(list);
                if (list[0]?.work_order) setSelectedWorkOrder(String(list[0].work_order));
            })
            .catch((error) => {
                console.error("Failed to load contractor attendance", error);
                // addAlert("Failed to load contractor attendance", "error");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [addAlert, organizationId, siteId]);

    const selectedRow = useMemo(
        () => rows.find((row) => String(row.work_order) === String(selectedWorkOrder)),
        [rows, selectedWorkOrder]
    );

    useEffect(() => {
        if (!selectedRow?.work_order) return;
        setForm({
            attendance_status: selectedRow.attendance_status === "Absent" ? "Absent" : "Present",
            person_count: selectedRow.person_count || 1,
            hours: selectedRow.hours || 8,
            scope_items: (selectedRow.scope_items || []).map((item) => item.id),
        });
        setLoadingDetails(true);
        ContractorApi.getWorkOrderDetail(selectedRow.work_order)
            .then((response) => {
                setDetails(response?.data || response);
            })
            .catch((error) => {
                console.error("Failed to load work order detail", error);
                // addAlert("Failed to load work order scope", "error");
            })
            .finally(() => setLoadingDetails(false));
    }, [addAlert, selectedRow]);

    const toggleScope = (id) => {
        setForm((current) => ({
            ...current,
            scope_items: current.scope_items.includes(id)
                ? current.scope_items.filter((item) => item !== id)
                : [...current.scope_items, id],
        }));
    };

    const handleSubmit = async () => {
        if (!selectedRow) return;
        setSubmitting(true);
        try {
            if (selectedRow.id) {
                await ContractorApi.updateAttendance(selectedRow.id, {
                    scope_items: form.scope_items,
                    date: selectedRow.date || todayISO(),
                    hours: toNumber(form.hours),
                });
            } else {
                await ContractorApi.createAttendance({
                    contractor_id: selectedRow.contractor_id,
                    work_order_id: selectedRow.work_order,
                    scope_items: form.scope_items,
                    person_count: toNumber(form.person_count, 1),
                    date: selectedRow.date || todayISO(),
                    hours: toNumber(form.hours),
                    attendance_status: form.attendance_status,
                });
            }
            addAlert("Contractor attendance saved", "success");
            onSaved();
        } catch (error) {
            console.error("Contractor attendance reminder failed", error);
            addAlert(error.message || "Failed to save contractor attendance", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="space-y-4">
                {!siteId && <EmptyState>This reminder is missing a site.</EmptyState>}
                {siteId && loading && <EmptyState>Loading contractor attendance...</EmptyState>}
                {siteId && !loading && !rows.length && <EmptyState>No contractor work orders found for this site today.</EmptyState>}
                {rows.length > 0 && (
                    <>
                        <Field label="Work order">
                            <select
                                value={selectedWorkOrder}
                                onChange={(event) => setSelectedWorkOrder(event.target.value)}
                                className={fieldClass}
                            >
                                {rows.map((row) => (
                                    <option key={row.work_order} value={row.work_order}>
                                        {row.work_order_id || `WO ${row.work_order}`} · {row.contractor_name} · {row.attendance_status}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <div className="grid grid-cols-3 gap-3">
                            <Field label="Status">
                                <select
                                    value={form.attendance_status}
                                    disabled={Boolean(selectedRow?.id)}
                                    onChange={(event) => setForm((current) => ({ ...current, attendance_status: event.target.value }))}
                                    className={fieldClass}
                                >
                                    <option>Present</option>
                                    <option>Absent</option>
                                </select>
                            </Field>
                            <Field label="Persons">
                                <input
                                    type="number"
                                    min="1"
                                    disabled={Boolean(selectedRow?.id)}
                                    value={form.person_count}
                                    onChange={(event) => setForm((current) => ({ ...current, person_count: event.target.value }))}
                                    className={fieldClass}
                                />
                            </Field>
                            <Field label="Hours">
                                <input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={form.hours}
                                    onChange={(event) => setForm((current) => ({ ...current, hours: event.target.value }))}
                                    className={fieldClass}
                                />
                            </Field>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <SectionTitle>Scope of work</SectionTitle>
                            {loadingDetails ? (
                                <p className="text-sm text-gray-500">Loading scope...</p>
                            ) : unwrapList(details?.scope_of_work).length ? (
                                <div className="space-y-2">
                                    {unwrapList(details?.scope_of_work).map((scope) => (
                                        <label key={scope.id} className="flex items-start gap-3 rounded-lg border border-gray-100 p-3">
                                            <input
                                                type="checkbox"
                                                checked={form.scope_items.includes(scope.id)}
                                                onChange={() => toggleScope(scope.id)}
                                                className="mt-0.5 size-4"
                                            />
                                            <span className="min-w-0">
                                                <span className="block text-sm font-semibold text-gray-900">{scope.item_name}</span>
                                                <span className="block text-xs text-gray-500">
                                                    {scope.quantity} {scope.unit} · ₹{toNumber(scope.total_price).toLocaleString("en-IN")}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No scope items available.</p>
                            )}
                        </div>
                    </>
                )}
            </div>
            <SubmitBar
                onCancel={onClose}
                onSubmit={handleSubmit}
                disabled={!selectedRow}
                submitting={submitting}
                submitLabel="Save attendance"
            />
        </>
    );
}

function VendorPaymentForm({ organizationId, onClose, onSaved }) {
    const { addAlert } = useAlert();
    const [mode, setMode] = useState("advance");
    const [vendors, setVendors] = useState([]);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [vendorId, setVendorId] = useState("");
    const [poId, setPoId] = useState("");
    const [amount, setAmount] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [paymentDate, setPaymentDate] = useState(todayISO());
    const [remarks, setRemarks] = useState("");
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!organizationId) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([
            fetch(`${BASE_URL}/inventory/list-org-vendors/${organizationId}/`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            }).then((response) => response.json()),
            ProcurementApi.listPurchaseOrders(organizationId).catch(() => []),
        ])
            .then(([vendorResponse, poResponse]) => {
                if (cancelled) return;
                setVendors(unwrapList(vendorResponse));
                const rawPOs = unwrapList(poResponse);
                setPurchaseOrders(mapPurchaseOrders(rawPOs));
            })
            .catch((error) => {
                console.error("Failed to load vendor payment data", error);
                // addAlert("Failed to load vendors", "error");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [addAlert, organizationId]);

    const filteredPOs = purchaseOrders.filter((po) => String(po.vendor || po.vendor_id) === String(vendorId));
    const selectedPO = filteredPOs.find((po) => String(po.id) === String(poId));
    const selectedPODue = selectedPO
        ? Math.max(0, toNumber(selectedPO.remaining_payment ?? selectedPO.balance_due ?? selectedPO.total_price - selectedPO.paid_amount))
        : 0;

    const handleSubmit = async () => {
        if (!vendorId || !amount || toNumber(amount) <= 0 || (mode === "po" && !poId)) {
            addAlert("Please complete the payment details", "error");
            return;
        }
        if (mode === "po" && toNumber(amount) > selectedPODue) {
            addAlert(`Payment cannot exceed purchase order due amount of ${formatCurrency(selectedPODue)}`, "error");
            return;
        }

        setSubmitting(true);
        try {
            const formData = new FormData();
            if (mode === "advance") {
                formData.append("vendor", vendorId);
                formData.append("amount", amount);
                formData.append("payment_date", paymentDate);
                formData.append("payment_method", paymentMethod);
                formData.append("remarks", remarks);
                if (file) formData.append("receipt", file);
                const response = await fetch(`${BASE_URL}/inventory/create-vendor-advance-payment/`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${getToken()}` },
                    body: formData,
                });
                const data = await response.json().catch(() => null);
                const feedback = resolveTransactionFeedback(data, {
                    successMessage: "Advance payment recorded successfully",
                    errorMessage: "Failed to record advance payment",
                });
                if (!response.ok || !feedback.success) throw new Error(feedback.message);
                addAlert(feedback.message, feedback.alertType);
            } else {
                formData.append("purchase_order", poId);
                formData.append("paid_amount", amount);
                formData.append("payment_method", paymentMethod);
                formData.append("remarks", remarks);
                if (file) formData.append("receipt_image", file);
                const response = await fetch(`${BASE_URL}/inventory/create-partpayment/`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${getToken()}` },
                    body: formData,
                });
                const data = await response.json().catch(() => null);
                const feedback = resolveTransactionFeedback(data, {
                    successMessage: "Part payment added successfully",
                    errorMessage: "Failed to add part payment",
                });
                if (!response.ok || !feedback.success) throw new Error(feedback.message);
                addAlert(feedback.message, feedback.alertType);
            }
            onSaved();
        } catch (error) {
            console.error("Vendor payment reminder failed", error);
            addAlert(error.message || "Failed to save vendor payment", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="space-y-4">
                {loading ? (
                    <EmptyState>Loading vendors...</EmptyState>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                ["advance", "Advance Payment"],
                                ["po", "Purchase Order Payment"],
                            ].map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    onClick={() => setMode(value)}
                                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                                        mode === value
                                            ? "border-[#3B0069] bg-[#ECE6F0] text-[#3B0069]"
                                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <Field label="Vendor">
                            <select
                                value={vendorId}
                                onChange={(event) => {
                                    setVendorId(event.target.value);
                                    setPoId("");
                                }}
                                className={fieldClass}
                            >
                                <option value="">Select vendor</option>
                                {vendors.map((vendor) => (
                                    <option key={vendor.id} value={vendor.id}>
                                        {vendor.name || vendor.vendor_name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        {mode === "po" && (
                            <>
                                <Field label="Purchase order">
                                    <select value={poId} onChange={(event) => setPoId(event.target.value)} className={fieldClass}>
                                        <option value="">Select purchase order</option>
                                        {filteredPOs.map((po) => (
                                            <option key={po.id} value={po.id}>
                                                {po.po_number || `PO #${po.id}`} · Due {formatCurrency(po.remaining_payment ?? po.balance_due ?? po.total_price - po.paid_amount)}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                {selectedPO && (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                        Due amount: <span className="font-semibold">{formatCurrency(selectedPODue)}</span>
                                    </div>
                                )}
                            </>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Payment date">
                                <input type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} className={fieldClass} />
                            </Field>
                            <Field label="Amount">
                                <div className="relative">
                                    <IndianRupee className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="number"
                                        value={amount}
                                        max={mode === "po" && selectedPO ? selectedPODue : undefined}
                                        onChange={(event) => setAmount(event.target.value)}
                                        className={`w-full rounded-lg border border-gray-200 bg-white py-2 text-sm outline-none transition focus:border-[#3B0069] focus:ring-2 focus:ring-[#3B0069]/10 disabled:bg-gray-100 disabled:text-gray-400 pl-9`}
                                    />
                                </div>
                            </Field>
                        </div>
                        <Field label="Payment method">
                            <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} className={fieldClass}>
                                <option>Cash</option>
                                <option>Online</option>
                                <option>Cheque</option>
                            </select>
                        </Field>
                        <Field label="Remarks">
                            <textarea value={remarks} onChange={(event) => setRemarks(event.target.value)} className={`${fieldClass} min-h-[88px] resize-none`} />
                        </Field>
                        <Field label="Receipt">
                            <input type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} className={fieldClass} />
                        </Field>
                    </>
                )}
            </div>
            <SubmitBar
                onCancel={onClose}
                onSubmit={handleSubmit}
                disabled={!vendorId || !amount || (mode === "po" && !poId)}
                submitting={submitting}
                submitLabel="Save payment"
            />
        </>
    );
}

function ContractorPaymentForm({ reminder, organizationId, onClose, onSaved }) {
    const siteId = reminder?.site ? String(reminder.site) : "";
    const reminderSiteName = getReminderSiteName(reminder);
    const { addAlert } = useAlert();
    const [contractors, setContractors] = useState([]);
    const [workOrders, setWorkOrders] = useState([]);
    const [details, setDetails] = useState(null);
    const [contractorId, setContractorId] = useState("");
    const [workOrderId, setWorkOrderId] = useState("");
    const [amount, setAmount] = useState("");
    const [paymentMode, setPaymentMode] = useState("Cash");
    const [paymentDate, setPaymentDate] = useState(todayISO());
    const [reference, setReference] = useState("");
    const [remarks, setRemarks] = useState("");
    const [milestoneId, setMilestoneId] = useState("");
    const [monthIndex, setMonthIndex] = useState("");
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!organizationId) return;
        let cancelled = false;
        setLoading(true);
        ContractorApi.fetchContractors({ organization_id: organizationId })
            .then((response) => {
                if (!cancelled) setContractors(unwrapList(response));
            })
            .catch((error) => {
                console.error("Failed to load contractors", error);
                // addAlert("Failed to load contractors", "error");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [addAlert, organizationId]);

    useEffect(() => {
        if (!contractorId) {
            setWorkOrders([]);
            return;
        }
        ContractorApi.getWorkOrdersByContractor(contractorId)
            .then((response) => {
                const list = unwrapList(response);
                setWorkOrders(
                    siteId
                        ? list.filter((order) => {
                              const siteIdMatches = [order.site_id, order.project_id, order.site_id_id].some(
                                  (value) => value !== undefined && value !== null && String(value) === siteId
                              );
                              const orderSiteName = String(order.site_name || order.site || "").trim().toLowerCase();
                              const siteNameMatches = reminderSiteName && orderSiteName === reminderSiteName;
                              const hasOnlySiteName = orderSiteName && ![order.site_id, order.project_id, order.site_id_id].some(Boolean);
                              return siteIdMatches || siteNameMatches || (hasOnlySiteName && !reminderSiteName);
                          })
                        : list
                );
            })
            .catch((error) => {
                console.error("Failed to load work orders", error);
                // addAlert("Failed to load work orders", "error");
            });
    }, [addAlert, contractorId, reminderSiteName, siteId]);

    useEffect(() => {
        if (!workOrderId) {
            setDetails(null);
            setMilestoneId("");
            setMonthIndex("");
            setAmount("");
            return;
        }
        setMilestoneId("");
        setMonthIndex("");
        setAmount("");
        ContractorApi.getWorkOrderDetail(workOrderId)
            .then((response) => setDetails(response?.data || response))
            .catch((error) => {
                console.error("Failed to load work order details", error);
                // addAlert("Failed to load work order details", "error");
            });
    }, [addAlert, workOrderId]);

    const handleMilestone = (id) => {
        setMilestoneId(id);
        const milestone = unwrapList(details?.milestones).find((item) => String(item.id) === String(id));
        if (milestone) setAmount(Math.max(0, toNumber(milestone.amount) - toNumber(milestone.paid_so_far)).toString());
    };

    const handleMonth = (value) => {
        setMonthIndex(value);
        const month = unwrapList(details?.monthly_bills)[Number(value) - 1];
        if (month) setAmount(Math.max(0, toNumber(month.amount) - toNumber(month.paid_so_far)).toString());
    };

    const handleAttachment = (selectedFile) => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(selectedFile);
        setPreviewUrl(selectedFile?.type?.startsWith("image/") ? URL.createObjectURL(selectedFile) : null);
    };

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const selectedMilestone = unwrapList(details?.milestones).find((item) => String(item.id) === String(milestoneId));
    const selectedMonth = monthIndex ? unwrapList(details?.monthly_bills)[Number(monthIndex) - 1] : null;
    const paymentLimit = (() => {
        if (!details) return null;
        if (details.payment_structure === "MILESTONE_BASED") {
            return selectedMilestone ? Math.max(0, toNumber(selectedMilestone.amount) - toNumber(selectedMilestone.paid_so_far)) : null;
        }
        if (details.payment_structure === "MONTHLY_RUNNING_BILL") {
            return selectedMonth ? Math.max(0, toNumber(selectedMonth.amount) - toNumber(selectedMonth.paid_so_far)) : null;
        }
        return Math.max(
            0,
            toNumber(
                details?.financial_summary?.balance_due ??
                    details?.balance_due ??
                    details?.total_due ??
                    details?.total_amount - details?.paid_amount
            )
        );
    })();

    const handleSubmit = async () => {
        if (!workOrderId || !amount || toNumber(amount) <= 0) {
            addAlert("Please complete payment details", "error");
            return;
        }
        if (details?.payment_structure === "MILESTONE_BASED" && !milestoneId) {
            addAlert("Please select a milestone", "error");
            return;
        }
        if (details?.payment_structure === "MONTHLY_RUNNING_BILL" && !monthIndex) {
            addAlert("Please select a month", "error");
            return;
        }
        if (paymentLimit !== null && toNumber(amount) > paymentLimit) {
            addAlert(`Payment cannot exceed due amount of ${formatCurrency(paymentLimit)}`, "error");
            return;
        }
        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append("work_order", details?.id || workOrderId);
            formData.append("amount", toNumber(amount));
            formData.append("payment_mode", paymentMode);
            formData.append("payment_date", paymentDate);
            formData.append("reference_no", reference);
            formData.append("remarks", remarks || "Contractor payment");
            if (milestoneId) formData.append("milestone", milestoneId);
            if (monthIndex) formData.append("month_index", monthIndex);
            if (file) formData.append("attachment_file", file);

            const response = await ContractorApi.createPayment(formData);
            const feedback = resolveTransactionFeedback(response, {
                successMessage: "Payment recorded successfully",
                errorMessage: "Failed to record payment",
            });
            if (!feedback.success) throw new Error(feedback.message);
            addAlert(feedback.message, feedback.alertType);
            onSaved();
        } catch (error) {
            console.error("Contractor payment reminder failed", error);
            addAlert(error.message || "Failed to save contractor payment", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="space-y-4">
                {loading ? (
                    <EmptyState>Loading contractors...</EmptyState>
                ) : (
                    <>
                        <Field label="Contractor">
                            <select
                                value={contractorId}
                                onChange={(event) => {
                                    setContractorId(event.target.value);
                                    setWorkOrderId("");
                                }}
                                className={fieldClass}
                            >
                                <option value="">Select contractor</option>
                                {contractors.map((contractor) => (
                                    <option key={contractor.id} value={contractor.id}>
                                        {contractor.name || contractor.contractor_name}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Work order">
                            <select value={workOrderId} onChange={(event) => setWorkOrderId(event.target.value)} className={fieldClass}>
                                <option value="">Select work order</option>
                                {workOrders.map((order) => (
                                    <option key={order.workorder_id || order.id} value={order.workorder_id || order.id}>
                                        {order.workorder_no || order.work_order_id || `WO #${order.id}`} · {order.site_name || order.site || "Site"}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        {details?.payment_structure === "MILESTONE_BASED" && (
                            <Field label="Milestone">
                                <select value={milestoneId} onChange={(event) => handleMilestone(event.target.value)} className={fieldClass}>
                                    <option value="">Select milestone</option>
                                    {unwrapList(details.milestones).map((milestone) => (
                                        <option key={milestone.id} value={milestone.id}>
                                            {milestone.name} · Balance ₹
                                            {Math.max(0, toNumber(milestone.amount) - toNumber(milestone.paid_so_far)).toLocaleString("en-IN")}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        )}
                        {details?.payment_structure === "MONTHLY_RUNNING_BILL" && (
                            <Field label="Month">
                                <select value={monthIndex} onChange={(event) => handleMonth(event.target.value)} className={fieldClass}>
                                    <option value="">Select month</option>
                                    {unwrapList(details.monthly_bills).map((bill, index) => {
                                        const balance = Math.max(0, toNumber(bill.amount) - toNumber(bill.paid_so_far));
                                        const disabled = balance <= 0 || String(bill.status || "").toUpperCase() === "PAID";
                                        return (
                                            <option key={`${bill.month}-${index}`} value={index + 1} disabled={disabled}>
                                                {bill.month || `Month ${index + 1}`} · Balance {formatCurrency(balance)}
                                            </option>
                                        );
                                    })}
                                </select>
                            </Field>
                        )}
                        {paymentLimit !== null && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                                Available to pay: <span className="font-semibold">{formatCurrency(paymentLimit)}</span>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Payment date">
                                <input type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} className={fieldClass} />
                            </Field>
                            <Field label="Amount">
                                <input
                                    type="number"
                                    value={amount}
                                    max={paymentLimit ?? undefined}
                                    onChange={(event) => setAmount(event.target.value)}
                                    className={fieldClass}
                                />
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Payment mode">
                                <select value={paymentMode} onChange={(event) => setPaymentMode(event.target.value)} className={fieldClass}>
                                    <option>Cash</option>
                                    <option>Online</option>
                                    <option>Cheque</option>
                                    <option>Bank Transfer</option>
                                </select>
                            </Field>
                            <Field label="Reference">
                                <input value={reference} onChange={(event) => setReference(event.target.value)} className={fieldClass} />
                            </Field>
                        </div>
                        <Field label="Remarks">
                            <textarea value={remarks} onChange={(event) => setRemarks(event.target.value)} className={`${fieldClass} min-h-[88px] resize-none`} />
                        </Field>
                        <AttachmentPicker
                            label="Attachment"
                            file={file}
                            previewUrl={previewUrl}
                            accept="application/pdf,image/*"
                            onChange={handleAttachment}
                            onRemove={() => handleAttachment(null)}
                        />
                    </>
                )}
            </div>
            <SubmitBar
                onCancel={onClose}
                onSubmit={handleSubmit}
                disabled={
                    !workOrderId ||
                    !amount ||
                    (details?.payment_structure === "MILESTONE_BASED" && !milestoneId) ||
                    (details?.payment_structure === "MONTHLY_RUNNING_BILL" && !monthIndex)
                }
                submitting={submitting}
                submitLabel="Save payment"
            />
        </>
    );
}

function WorkerPaymentForm({ reminder, onClose, onSaved }) {
    const siteId = useSiteGuard(reminder?.site);
    const { addAlert } = useAlert();
    const [workers, setWorkers] = useState([]);
    const [selected, setSelected] = useState([]);
    const [amounts, setAmounts] = useState({});
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [paymentDate, setPaymentDate] = useState(todayISO());
    const [remarks, setRemarks] = useState("");
    const [categoryId, setCategoryId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!siteId) return;
        let cancelled = false;
        setLoading(true);
        Promise.all([
            fetch(`${BASE_URL}/worker/workers-page/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${getToken()}`,
                },
                body: JSON.stringify({ worker_type: "Worker", sites: siteId }),
            }).then((response) => response.json()),
            fetch(`${BASE_URL}/finance/get-all-finance-catagory/`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            }).then((response) => response.json()),
        ])
            .then(([workerResponse, categoryResponse]) => {
                if (cancelled) return;
                setWorkers(unwrapList(workerResponse));
                const workerCategory = unwrapList(categoryResponse).find((category) => category.catagory_name === "Worker Transaction");
                setCategoryId(workerCategory?.id || null);
            })
            .catch((error) => {
                console.error("Failed to load worker payment data", error);
                // addAlert("Failed to load workers", "error");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [addAlert, siteId]);

    const handleSelect = (id) => {
        setSelected((current) =>
            current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
        );
    };

    const handleSubmit = async () => {
        if (!selected.length || selected.some((id) => toNumber(amounts[id]) <= 0)) {
            addAlert("Select worker and enter payment amount", "error");
            return;
        }

        setSubmitting(true);
        try {
            await Promise.all(
                selected.map((id) => {
                    const worker = workers.find((item) => String(getId(item, ["worker_id", "id"])) === String(id));
                    const formData = new FormData();
                    formData.append("payment_type", "Wage");
                    formData.append("amount", toNumber(amounts[id]));
                    formData.append("worker_id", id);
                    formData.append("transaction_date", paymentDate);
                    formData.append("transaction_method", paymentMethod);
                    formData.append("comments", remarks || `Worker payment - ${worker?.worker_name || worker?.name || id}`);
                    if (categoryId) formData.append("transaction_catagory_id", categoryId);
                    formData.append("site", siteId);

                    return fetch(`${BASE_URL}/finance/create-worker-transaction/`, {
                        method: "POST",
                        headers: { Authorization: `Bearer ${getToken()}` },
                        body: formData,
                    }).then(async (response) => {
                        const data = await response.json().catch(() => null);
                        const feedback = resolveTransactionFeedback(data, {
                            successMessage: "Payment created successfully",
                            errorMessage: "Failed to create payment",
                        });
                        if (!response.ok || !feedback.success) throw new Error(feedback.message);
                    });
                })
            );
            addAlert("Worker payment saved", "success");
            onSaved();
        } catch (error) {
            console.error("Worker payment reminder failed", error);
            addAlert(error.message || "Failed to save worker payment", "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <div className="space-y-4">
                {!siteId && <EmptyState>This reminder is missing a site.</EmptyState>}
                {siteId && loading && <EmptyState>Loading workers...</EmptyState>}
                {siteId && !loading && (
                    <>
                        <div className="rounded-xl border border-gray-200 bg-white p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex size-10 items-center justify-center rounded-lg bg-[#ECE6F0] text-[#3B0069]">
                                    <Users className="size-5" />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900">Multiple worker payment</p>
                                    <p className="text-xs text-gray-500">Select one or more workers and enter the amount for each.</p>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Payment date">
                                <input type="date" value={paymentDate} onChange={(event) => setPaymentDate(event.target.value)} className={fieldClass} />
                            </Field>
                            <Field label="Payment method">
                                <select value={paymentMethod} onChange={(event) => setPaymentMethod(event.target.value)} className={fieldClass}>
                                    <option>Cash</option>
                                    <option>Online</option>
                                    <option>Cheque</option>
                                </select>
                            </Field>
                        </div>
                        <div className="space-y-3">
                            {workers.map((worker) => {
                                const id = getId(worker, ["worker_id", "id"]);
                                const checked = selected.includes(id);
                                return (
                                    <div key={id} className="grid grid-cols-[24px_minmax(0,1fr)_130px] items-center gap-3 rounded-xl border border-gray-200 bg-white p-3.5">
                                        <input type="checkbox" checked={checked} onChange={() => handleSelect(id)} />
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold text-gray-900">{worker.worker_name || worker.name}</p>
                                            <p className="truncate text-xs text-gray-500">{worker.worker_workname || worker.worker_workcategory || "Worker"}</p>
                                            <p className="mt-0.5 text-xs font-semibold text-amber-700">
                                                Total due: {formatCurrency(worker.total_due)}
                                            </p>
                                        </div>
                                        <input
                                            type="number"
                                            disabled={!checked}
                                            max={Math.max(0, toNumber(worker.total_due))}
                                            value={amounts[id] || ""}
                                            onChange={(event) =>
                                                setAmounts((current) => ({ ...current, [id]: event.target.value }))
                                            }
                                            placeholder="Amount"
                                            className={fieldClass}
                                        />
                                    </div>
                                );
                            })}
                            {!workers.length && <EmptyState>No workers found.</EmptyState>}
                        </div>
                        <Field label="Remarks">
                            <textarea value={remarks} onChange={(event) => setRemarks(event.target.value)} className={`${fieldClass} min-h-[88px] resize-none`} />
                        </Field>
                    </>
                )}
            </div>
            <SubmitBar
                onCancel={onClose}
                onSubmit={handleSubmit}
                disabled={!siteId || !selected.length}
                submitting={submitting}
                submitLabel="Save payment"
            />
        </>
    );
}

function UnsupportedForm({ onClose }) {
    return (
        <>
            <EmptyState>This action type does not have a dashboard action flow yet.</EmptyState>
            <SubmitBar onCancel={onClose} onSubmit={onClose} submitting={false} submitLabel="Close" />
        </>
    );
}

export const getReminderActionLabel = (actionType) => ACTION_BUTTON_LABELS[actionType] || "";

export const getCompletedReminderActionLabel = (actionType) => COMPLETED_ACTION_BUTTON_LABELS[actionType] || "";

export default function ReminderActionDrawer({ reminder, reminders = [], organizationId, onClose, onSaved }) {
    const [currentIndex, setCurrentIndex] = useState(0);

    const flowReminders = useMemo(() => {
        if (reminder && getReminderCompleted(reminder)) return [reminder];
        const actionable = reminders.filter((item) => ACTION_BUTTON_LABELS[item?.action_type]);
        if (!actionable.length && reminder) return [reminder];
        return actionable;
    }, [reminder, reminders]);

    useEffect(() => {
        if (!reminder) return;
        const nextIndex = flowReminders.findIndex((item) => String(item?.id) === String(reminder?.id));
        setCurrentIndex(nextIndex >= 0 ? nextIndex : 0);
    }, [flowReminders, reminder]);

    const currentReminder = reminder ? flowReminders[currentIndex] || reminder : null;
    const actionType = currentReminder?.action_type;
    const currentSiteId = useSiteGuard(currentReminder?.site);
    const accessState = useReminderActionAccess({
        actionType,
        organizationId,
        siteId: currentSiteId,
    });

    if (!reminder) return null;

    const isCompletedReminder = getReminderCompleted(currentReminder);
    const handleSaved = () => {
        if (isCompletedReminder) {
            onSaved?.({
                done: true,
                completedReminder: currentReminder,
            });
            return;
        }
        const nextIndex = currentIndex + 1;
        if (nextIndex < flowReminders.length) {
            setCurrentIndex(nextIndex);
            onSaved?.({
                done: false,
                completedReminder: currentReminder,
                nextReminder: flowReminders[nextIndex],
            });
            return;
        }

        onSaved?.({
            done: true,
            completedReminder: currentReminder,
        });
    };
    const commonProps = { reminder: currentReminder, organizationId, onClose, onSaved: handleSaved };
    const titleLabel = isCompletedReminder
        ? COMPLETED_ACTION_BUTTON_LABELS[actionType] || ACTION_BUTTON_LABELS[actionType]
        : ACTION_BUTTON_LABELS[actionType];
    const title = titleLabel
        ? `${titleLabel.replace(/^\w/, (value) => value.toUpperCase())}`
        : "Action item";
    const subtitle = [currentReminder.title, currentReminder.site_name || currentReminder.scope].filter(Boolean).join(" · ");

    let content = <UnsupportedForm onClose={onClose} />;
    if (actionType === "INVENTORY_UPDATE") content = <InventoryUpdateForm {...commonProps} />;
    if (actionType === "TASK_UPDATE") content = <TaskUpdateForm {...commonProps} type="TASK_UPDATE" />;
    if (actionType === "SUBTASK_UPDATE") content = <TaskUpdateForm {...commonProps} type="SUBTASK_UPDATE" />;
    if (actionType === "WORKER_ATTENDANCE") content = <WorkerAttendanceForm {...commonProps} />;
    if (actionType === "CONTRACTOR_ATTENDANCE") content = <ContractorAttendanceForm {...commonProps} />;
    if (actionType === "VENDOR_PAYMENT") content = <VendorPaymentForm {...commonProps} />;
    if (actionType === "CONTRACTOR_PAYMENT") content = <ContractorPaymentForm {...commonProps} />;
    if (actionType === "WORKER_PAYMENT") content = <WorkerPaymentForm {...commonProps} />;

    if (accessState.loading) {
        content = <AccessLoadingState onClose={onClose} />;
    } else if (!accessState.allowed && accessState.requirement) {
        content = (
            <AccessBanner
                requirement={accessState.requirement}
                error={accessState.error}
                siteName={currentReminder?.site_name || currentReminder?.scope}
                onClose={onClose}
            />
        );
    }

    return (
        <DrawerShell
            title={title}
            subtitle={subtitle}
            onClose={onClose}
            currentIndex={currentIndex}
            total={flowReminders.length}
            onPrevious={() => setCurrentIndex((value) => Math.max(0, value - 1))}
            onNext={() => setCurrentIndex((value) => Math.min(flowReminders.length - 1, value + 1))}
            showNavigation={!isCompletedReminder && flowReminders.length > 1}
        >
            <div key={currentReminder?.id || actionType}>{content}</div>
        </DrawerShell>
    );
}
